import React, { useState } from 'react';
import { Settings, Network, Cloud, Mail, ShieldCheck, Monitor, GraduationCap, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import ServiceModal from '../components/ServiceModal';

const BusinessServicesPage = ({ language }) => {
  const [activeModal, setActiveModal] = useState(null);
  const translations = {
    de: {
      title: 'IT-Services für lokale Unternehmen',
      subtitle: 'Professionelle IT-Lösungen für Ihren Geschäftserfolg',
      services: [
        {
          icon: Settings,
          title: 'Regelmäßige Systempflege & Fernwartung',
          description: 'Schnelle Hilfe ohne Anfahrt – ich halte Ihre Systeme aktuell und kümmere mich um Probleme, bevor sie entstehen.'
        },
        {
          icon: Network,
          title: 'Netzwerk- & Routereinrichtung',
          description: 'Stabile, sichere Verbindungen im Büro – professionelle Planung und Umsetzung Ihrer Netzwerkinfrastruktur.'
        },
        {
          icon: Cloud,
          title: 'Datensicherung & Cloud-Lösungen',
          description: 'Ihre Daten sind immer verfügbar und geschützt – automatisierte Backups und zuverlässige Cloud-Integration.'
        },
        {
          icon: Mail,
          title: 'E-Mail- & Cloud-Dienste',
          description: 'Einrichtung und Verwaltung von Microsoft 365, Google Workspace und anderen Cloud-Diensten – professionell und sicher.'
        },
        {
          icon: ShieldCheck,
          title: 'Sicherheitscheck & Virenschutz',
          description: 'Schutz vor Angriffen und Datenverlust – umfassende Sicherheitsanalysen und effektive Schutzmaßnahmen.'
        },
        {
          icon: Monitor,
          title: 'Kaufberatung & Einrichtung',
          description: 'Neue Rechner, Drucker oder Komplettarbeitsplätze – ich berate Sie kompetent und richte alles betriebsbereit ein.'
        },
        {
          icon: GraduationCap,
          title: 'Schulung & Beratung',
          description: 'Verständlich erklärt, exakt auf Ihre Anforderungen abgestimmt – damit Sie und Ihr Team effizient arbeiten können.'
        }
      ],
      benefits: {
        title: 'Ihre Vorteile',
        items: [
          'Minimale Ausfallzeiten durch proaktive Wartung',
          'Flexible Fernwartung spart Zeit und Kosten',
          'Skalierbare Lösungen, die mit Ihrem Unternehmen wachsen',
          'Persönlicher Ansprechpartner für alle IT-Fragen'
        ]
      },
      cta: {
        title: 'Bereit für professionelle IT-Betreuung?',
        description: 'Lassen Sie uns gemeinsam die beste Lösung für Ihr Unternehmen finden',
        button: 'Beratungsgespräch vereinbaren'
      }
    },
    en: {
      title: 'IT Services for local Businesses',
      subtitle: 'Professional IT solutions for your business success',
      services: [
        {
          icon: Settings,
          title: 'Regular System Maintenance & Remote Support',
          description: 'Fast help without travel – we keep your systems up to date and take care of problems before they arise.'
        },
        {
          icon: Network,
          title: 'Network & Router Setup',
          description: 'Stable, secure connections in the office – professional planning and implementation of your network infrastructure.'
        },
        {
          icon: Cloud,
          title: 'Data Backup & Cloud Solutions',
          description: 'Your data is always available and protected – automated backups and reliable cloud integration.'
        },
        {
          icon: Mail,
          title: 'Email & Cloud Services',
          description: 'Setup and management of Microsoft 365, Google Workspace and other cloud services – professional and secure.'
        },
        {
          icon: ShieldCheck,
          title: 'Security Check & Virus Protection',
          description: 'Protection against attacks and data loss – comprehensive security analyses and effective protective measures.'
        },
        {
          icon: Monitor,
          title: 'Purchase Advice & Setup',
          description: 'New computers, printers or complete workstations – we advise you competently and set everything up ready for operation.'
        },
        {
          icon: GraduationCap,
          title: 'Training & Consulting',
          description: 'Clearly explained, precisely tailored to your requirements – so you and your team can work efficiently.'
        }
      ],
      benefits: {
        title: 'Your Benefits',
        items: [
          'Minimal downtime through proactive maintenance',
          'Flexible remote support saves time and costs',
          'Scalable solutions that grow with your business',
          'Personal contact for all IT questions'
        ]
      },
      cta: {
        title: 'Ready for Professional IT Support?',
        description: 'Let us find the best solution for your business together',
        button: 'Schedule Consultation'
      }
    }
  };

  const t = translations[language];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-4" style={{ color: '#E53D4A' }}>
              {t.title}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              {t.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {t.services.map((service, index) => {
              const IconComponent = service.icon;
              // All 7 business services now have story-tellings
              const modalTypes = ['businessMaintenance', 'businessNetwork', 'businessBackup', 'businessEmail', 'businessSecurity', 'businessPurchase', 'businessTraining'];
              const modalType = modalTypes[index];
              
              return (
                <Card 
                  key={index} 
                  className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                  onClick={() => setActiveModal(modalType)}
                >
                  <CardHeader>
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#E53D4A] flex items-center justify-center">
                        <IconComponent className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-xl mb-2 flex items-center justify-between" style={{ color: '#E53D4A' }}>
                          <span>{service.title}</span>
                          <span className="text-xs bg-[#E53D4A] text-white px-2 py-1 rounded-full">
                            {language === 'de' ? 'Details' : 'Details'}
                          </span>
                        </CardTitle>
                        <CardDescription className="text-base">
                          {service.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12" style={{ color: '#E53D4A' }}>
              {t.benefits.title}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {t.benefits.items.map((benefit, index) => (
                <Card key={index} className="bg-white dark:bg-gray-900">
                  <CardContent className="pt-6">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p className="text-gray-700 dark:text-gray-300">{benefit}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#E53D4A] text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-4">
              {t.cta.title}
            </h2>
            <p className="text-xl mb-8 text-gray-200">
              {t.cta.description}
            </p>
            <Button 
              size="lg" 
              className="bg-white text-[#E53D4A] hover:bg-gray-100"
              asChild
            >
              <a href="mailto:kontakt@martinez.systems">
                {t.cta.button}
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Service Modals */}
      {activeModal && (
        <ServiceModal 
          isOpen={true}
          onClose={() => setActiveModal(null)}
          language={language}
          serviceType={activeModal}
        />
      )}
    </div>
  );
};

export default BusinessServicesPage;
